if (!customElements.get('youtube-showcase')) {
  customElements.define(
    'youtube-showcase',
    class YoutubeShowcase extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.channelId = this.dataset.channelId;
        this.apiKey = this.dataset.apiKey;
        this.videoCount = parseInt(this.dataset.videoCount, 10) || 1;
        this.minVideoSeconds = parseInt(this.dataset.minVideoSeconds, 10);
        if (isNaN(this.minVideoSeconds)) this.minVideoSeconds = 180;
        this.cacheMinutes = parseInt(this.dataset.cacheMinutes, 10) || 30;

        this.statEls = this.querySelectorAll('[data-yt-stat]');
        this.videosEl = this.querySelector('[data-yt-videos]');
        this.avatarEl = this.querySelector('[data-yt-avatar]');
        this.watchFallbackEl = this.querySelector('[data-yt-watch-fallback]');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // If the avatar image ever fails to load, hide it rather than show a broken icon.
        if (this.avatarEl) {
          this.avatarEl.addEventListener('error', () => {
            const wrap = this.avatarEl.closest('.yt-showcase__avatar') || this.avatarEl;
            wrap.style.display = 'none';
          });
        }

        this.targets = {};
        this.animated = false;
        this.visible = false;

        this.setupObserver();
        this.loadData();
      }

      /* ---------- Caching (localStorage with TTL) ---------- */

      cacheKey(kind) {
        // Videos are sliced to videoCount before caching, so a settings change must
        // not serve a stale-length list — key the videos cache by count too.
        if (kind === 'videos') return `bgk-yt-videos-v2-${this.channelId}-${this.videoCount}-${this.minVideoSeconds}`;
        return `bgk-yt-${kind}-${this.channelId}`;
      }

      readCache(kind) {
        try {
          const raw = localStorage.getItem(this.cacheKey(kind));
          if (!raw) return null;
          const obj = JSON.parse(raw);
          if (!obj || typeof obj.t !== 'number') return null;
          if (Date.now() - obj.t > this.cacheMinutes * 60 * 1000) return null;
          return obj.d;
        } catch (e) {
          return null;
        }
      }

      writeCache(kind, data) {
        try {
          localStorage.setItem(this.cacheKey(kind), JSON.stringify({ t: Date.now(), d: data }));
        } catch (e) {
          /* private mode / quota — ignore, we just refetch next time */
        }
      }

      /* ---------- Data loading ---------- */

      async loadData() {
        if (!this.channelId || !this.apiKey) {
          this.hideVideos();
          return;
        }

        let stats = this.readCache('stats');
        if (!stats) {
          stats = await this.fetchStats();
          if (stats) this.writeCache('stats', stats);
        }

        if (stats) {
          this.targets.subscribers = parseInt(stats.subscriberCount, 10);
          this.targets.views = parseInt(stats.viewCount, 10);
          this.maybeAnimate();

          // Upgrade the avatar to the live channel image — but only commit it once
          // we know it actually loads. A stale/expired yt3.ggpht URL must never
          // replace the already-working static fallback. First paint is never empty.
          if (this.avatarEl && stats.avatar) {
            const probe = new Image();
            probe.onload = () => {
              this.avatarEl.src = stats.avatar;
            };
            probe.src = stats.avatar;
          }

          let videos = this.readCache('videos');
          if (!videos) {
            videos = await this.fetchVideos(stats.uploads);
            if (videos) this.writeCache('videos', videos);
          }
          if (videos && videos.length) {
            this.renderVideos(videos);
          } else {
            this.hideVideos();
          }
        } else {
          // API unavailable — keep the static fallback numbers, hide the video row.
          this.hideVideos();
        }
      }

      async fetchStats() {
        try {
          const url =
            'https://www.googleapis.com/youtube/v3/channels' +
            '?part=snippet,statistics,contentDetails' +
            `&id=${encodeURIComponent(this.channelId)}` +
            `&key=${encodeURIComponent(this.apiKey)}`;
          const res = await fetch(url);
          if (!res.ok) return null;
          const json = await res.json();
          const item = json.items && json.items[0];
          if (!item) return null;
          const thumbs = (item.snippet && item.snippet.thumbnails) || {};
          const avatar = (thumbs.high || thumbs.medium || thumbs.default || {}).url;
          return {
            subscriberCount: item.statistics.subscriberCount,
            viewCount: item.statistics.viewCount,
            avatar: avatar,
            uploads:
              item.contentDetails &&
              item.contentDetails.relatedPlaylists &&
              item.contentDetails.relatedPlaylists.uploads,
          };
        } catch (e) {
          return null;
        }
      }

      async fetchVideos(uploadsPlaylistId) {
        if (!uploadsPlaylistId) return null;
        try {
          // 1. Latest upload IDs, newest first. Over-fetch so we can skip Shorts
          //    (and any private/deleted items) and still fill the requested count.
          const plUrl =
            'https://www.googleapis.com/youtube/v3/playlistItems' +
            '?part=contentDetails' +
            '&maxResults=25' +
            `&playlistId=${encodeURIComponent(uploadsPlaylistId)}` +
            `&key=${encodeURIComponent(this.apiKey)}`;
          const plRes = await fetch(plUrl);
          if (!plRes.ok) return null;
          const plJson = await plRes.json();
          if (!plJson.items) return null;
          const ids = plJson.items
            .map((i) => i.contentDetails && i.contentDetails.videoId)
            .filter(Boolean);
          if (!ids.length) return null;

          // 2. Fetch durations + details for those IDs (one call, 1 quota unit).
          const vUrl =
            'https://www.googleapis.com/youtube/v3/videos' +
            '?part=snippet,contentDetails,statistics' +
            `&id=${ids.join(',')}` +
            `&key=${encodeURIComponent(this.apiKey)}`;
          const vRes = await fetch(vUrl);
          if (!vRes.ok) return null;
          const vJson = await vRes.json();
          if (!vJson.items) return null;
          const byId = {};
          vJson.items.forEach((it) => {
            byId[it.id] = it;
          });

          // 3. Walk newest-first, keep only long-form videos, stop at videoCount.
          const out = [];
          for (const id of ids) {
            const it = byId[id];
            if (!it) continue; // private/deleted — not returned by videos.list
            const s = it.snippet || {};
            const seconds = this.parseDuration(it.contentDetails && it.contentDetails.duration);
            const text = ((s.title || '') + ' ' + (s.description || '')).toLowerCase();
            const isShort = (seconds > 0 && seconds < this.minVideoSeconds) || text.indexOf('#shorts') !== -1;
            if (isShort) continue;
            const thumbs = s.thumbnails || {};
            const best = thumbs.maxres || thumbs.standard || thumbs.high || thumbs.medium || thumbs.default || {};
            if (!best.url) continue;
            out.push({
              id: id,
              title: s.title,
              thumb: best.url,
              publishedAt: s.publishedAt,
              viewCount: it.statistics && it.statistics.viewCount,
            });
            if (out.length >= this.videoCount) break;
          }
          return out;
        } catch (e) {
          return null;
        }
      }

      parseDuration(iso) {
        const m = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso || '');
        if (!m) return 0;
        return Number(m[1] || 0) * 3600 + Number(m[2] || 0) * 60 + Number(m[3] || 0);
      }

      formatCount(n) {
        if (typeof n !== 'number' || isNaN(n)) return '';
        const u = this.unitFor(n);
        // Video view count: whole units only, no decimals (42K, 43K, 1M).
        return Math.round(n / u.div) + u.suffix;
      }

      formatDate(iso) {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      }

      /* ---------- Video rendering (facade pattern) ---------- */

      renderVideos(videos) {
        if (!this.videosEl) return;
        const frag = document.createDocumentFragment();

        videos.forEach((v) => {
          const tile = document.createElement('div');
          tile.className = 'yt-showcase__video';

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'yt-showcase__video-btn';
          btn.setAttribute('aria-label', `Play video: ${v.title}`);

          const img = document.createElement('img');
          img.className = 'yt-showcase__video-thumb';
          img.src = v.thumb;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 480;
          img.height = 270;
          btn.appendChild(img);

          const play = document.createElement('span');
          play.className = 'yt-showcase__video-play';
          play.setAttribute('aria-hidden', 'true');
          play.innerHTML =
            '<svg viewBox="0 0 68 48" focusable="false"><path class="yt-play-bg" d="M66.5 7.7c-.8-2.9-3-5.1-5.9-5.9C55.3.5 34 .5 34 .5S12.7.5 7.4 1.8C4.5 2.6 2.3 4.8 1.5 7.7.2 13 .2 24 .2 24s0 11 1.3 16.3c.8 2.9 3 5.1 5.9 5.9C12.7 47.5 34 47.5 34 47.5s21.3 0 26.6-1.3c2.9-.8 5.1-3 5.9-5.9C67.8 35 67.8 24 67.8 24s0-11-1.3-16.3z"></path><path d="M27 34V14l18 10z" fill="#fff"></path></svg>';
          btn.appendChild(play);

          btn.addEventListener('click', () => this.playVideo(btn, v));
          tile.appendChild(btn);

          const title = document.createElement('span');
          title.className = 'yt-showcase__video-title';
          title.textContent = v.title;
          tile.appendChild(title);

          // Meta line: "1.2M views • Jun 10, 2026" (omitted gracefully if data missing)
          const metaParts = [];
          const views = this.formatCount(parseInt(v.viewCount, 10));
          if (views) metaParts.push(`${views} views`);
          const date = this.formatDate(v.publishedAt);
          if (date) metaParts.push(date);
          if (metaParts.length) {
            const meta = document.createElement('span');
            meta.className = 'yt-showcase__video-meta';
            meta.textContent = metaParts.join(' • ');
            tile.appendChild(meta);
          }

          frag.appendChild(tile);
        });

        this.videosEl.innerHTML = '';
        this.videosEl.appendChild(frag);
        this.videosEl.hidden = false;
        if (this.watchFallbackEl) this.watchFallbackEl.hidden = true;
      }

      playVideo(btn, v) {
        // Mobile browsers block UNMUTED autoplay, and iOS won't play inline without
        // playsinline. YouTube's player calls its internal video.play() async (after
        // the iframe loads), by which point the tap's user-gesture token is spent —
        // so on touch devices we must start muted (viewer taps the player's own
        // unmute control for sound). Desktop is unaffected and keeps sound on click.
        const needsMute = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        const params = 'autoplay=1&playsinline=1&rel=0' + (needsMute ? '&mute=1' : '');

        const iframe = document.createElement('iframe');
        iframe.className = 'yt-showcase__video-frame';
        iframe.src = `https://www.youtube-nocookie.com/embed/${v.id}?${params}`;
        iframe.title = v.title;
        iframe.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('playsinline', '');
        iframe.setAttribute('webkit-playsinline', '');
        btn.replaceWith(iframe);
        // Defer focus one frame — focusing synchronously can interfere with WebKit's
        // media start in the same activation frame.
        requestAnimationFrame(() => iframe.focus());
      }

      hideVideos() {
        if (this.videosEl) this.videosEl.hidden = true;
        // Right column shouldn't look sparse — offer a direct link instead.
        if (this.watchFallbackEl) this.watchFallbackEl.hidden = false;
      }

      /* ---------- Count-up animation ---------- */

      setupObserver() {
        if (!('IntersectionObserver' in window)) {
          this.visible = true;
          this.maybeAnimate();
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.visible = true;
                this.maybeAnimate();
                this.observer.disconnect();
              }
            });
          },
          { threshold: 0.3 }
        );
        const anchor = this.querySelector('.yt-showcase__stats') || this;
        this.observer.observe(anchor);
      }

      maybeAnimate() {
        if (this.animated) return;
        if (Object.keys(this.targets).length === 0) return; // data not ready yet
        if (!this.visible && !this.reduceMotion) return; // not scrolled into view yet
        this.animated = true;

        this.statEls.forEach((el) => {
          const kind = el.dataset.ytStat;
          const target = this.targets[kind];
          if (typeof target !== 'number' || isNaN(target)) return; // leave the static fallback
          this.countUp(el, target);
        });
      }

      unitFor(n) {
        if (n >= 1e9) return { div: 1e9, suffix: 'B', dp: 1 };
        if (n >= 1e6) return { div: 1e6, suffix: 'M', dp: 1 };
        if (n >= 1e3) return { div: 1e3, suffix: 'K', dp: n / 1e3 < 100 ? 1 : 0 };
        return { div: 1, suffix: '', dp: 0 };
      }

      countUp(el, target) {
        const u = this.unitFor(target);
        const fmt = (value) => {
          let s = (value / u.div).toFixed(u.dp);
          if (u.dp > 0) s = s.replace(/\.0$/, '');
          return s + u.suffix;
        };

        if (this.reduceMotion) {
          el.textContent = fmt(target);
          return;
        }

        const duration = 1600;
        const start = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          el.textContent = fmt(target * ease(p));
          if (p < 1) {
            requestAnimationFrame(tick);
          } else {
            el.textContent = fmt(target);
          }
        };
        requestAnimationFrame(tick);
      }
    }
  );
}
